import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import {
	getRevisionRequests,
	approveRevisionRequest,
	declineRevisionRequest,
} from "../api/revision-evaluation-api";
import type {
	EvaluationRevisionRequest,
	ApproveRevisionRequestPayload,
	DeclineRevisionRequestPayload,
} from "../types/revision-review.type";

const revisionRequestKeys = {
	all: (rfxId: number, evaluationId: number) =>
		["revision-requests", rfxId, evaluationId] as const,
	list: (rfxId: number, evaluationId: number) =>
		[...revisionRequestKeys.all(rfxId, evaluationId), "list"] as const,
};

export { revisionRequestKeys };

export const useRevisionRequests = (
	rfxId: number | undefined,
	evaluationId: number | undefined
): UseQueryResult<EvaluationRevisionRequest[]> => {
	return useQuery({
		queryKey: revisionRequestKeys.list(rfxId!, evaluationId!),
		queryFn: () => getRevisionRequests(rfxId!, evaluationId!),
		enabled: !!rfxId && !!evaluationId,
	});
};

export const useApproveRevisionRequest = (
	rfxId: number,
	evaluationId: number
): UseMutationResult<
	EvaluationRevisionRequest | null,
	Error,
	{ payload: ApproveRevisionRequestPayload; revisionRequestId: number; rfxInviteId: number; }
> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ rfxInviteId, revisionRequestId, payload }) =>
			approveRevisionRequest(rfxInviteId, revisionRequestId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: revisionRequestKeys.list(rfxId, evaluationId),
			});
		},
	});
};

export const useDeclineRevisionRequest = (
	rfxId: number,
	evaluationId: number
): UseMutationResult<
	EvaluationRevisionRequest | null,
	Error,
	{ payload: DeclineRevisionRequestPayload; revisionRequestId: number; rfxInviteId: number; }
> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ rfxInviteId, revisionRequestId, payload }) =>
			declineRevisionRequest(rfxInviteId, revisionRequestId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: revisionRequestKeys.list(rfxId, evaluationId),
			});
		},
	});
};
